import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export interface AdvertisedEvent {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl: string;
  organizerName?: string;
  organizerImage?: string;
  startDate: Date | string; // When the ad should start showing
  expirationDate: Date | string; // When the ad should stop showing
}

interface AdvertisedEventBannerProps {
  events: AdvertisedEvent[];
  onClose: () => void;
}

const AdvertisedEventBanner: React.FC<AdvertisedEventBannerProps> = ({ events, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  // Filter events based on current date
  const now = new Date();
  const activeEvents = events.filter(event => {
    const startDate = new Date(event.startDate);
    const expirationDate = new Date(event.expirationDate);
    return now >= startDate && now <= expirationDate;
  });

  // Auto-rotate through events every 5 seconds
  useEffect(() => {
    if (activeEvents.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activeEvents.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [activeEvents.length]);

  // Banner sizing ref + effect must be declared before any early returns so hooks order is stable
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) return;

    const setBannerHeight = () => {
      try {
        const h = el.clientHeight || Math.round(el.getBoundingClientRect().height) || 0;
        document.documentElement.style.setProperty('--banner-height', `${Math.round(h)}px`);
      } catch (e) {
        // ignore
      }
    };

    // initial
    setBannerHeight();

    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new (window as any).ResizeObserver(() => setBannerHeight());
      try { if (ro) ro.observe(el); } catch (e) { /* ignore */ }
    } else {
      window.addEventListener('resize', setBannerHeight);
    }

    return () => {
      try { document.documentElement.style.setProperty('--banner-height', '0px'); } catch (e) {}
      if (ro) try { ro.disconnect(); } catch (e) {}
      else window.removeEventListener('resize', setBannerHeight);
    };
  }, [activeEvents.length]);

  // Don't render if no active events
  if (activeEvents.length === 0) {
    return null;
  }

  const currentEvent = activeEvents[currentIndex];

  const handleBannerClick = (e: React.MouseEvent) => {
    console.log('🟢 Banner clicked', { target: e.target, currentTarget: e.currentTarget });
    // Check if it's an external URL (starts with http:// or https://)
    if (currentEvent.linkUrl.startsWith('http://') || currentEvent.linkUrl.startsWith('https://')) {
      // External URL - open in same window
      window.location.href = currentEvent.linkUrl;
    } else {
      // Internal path - use React Router
      navigate(currentEvent.linkUrl);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    console.log('🔴 AdvertisedEventBanner handleClose clicked');
    e.stopPropagation();
    console.log('🔴 Calling onClose callback');
    onClose();
    console.log('🔴 onClose callback completed');
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + activeEvents.length) % activeEvents.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % activeEvents.length);
  };


  return (
    <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg">
      <div
        className="relative cursor-pointer hover:opacity-95 transition-opacity"
        onClick={handleBannerClick}
      >
        {/* Background image with overlay */}
        <div className="relative h-32 md:h-40 overflow-hidden">
          <img
            src={currentEvent.imageUrl}
            alt={currentEvent.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/80 to-pink-600/80" />
        </div>

        {/* Organizer Image Overlay (top-left like EventCard) */}
        {currentEvent.organizerImage && (
          <div className="absolute top-2 left-2 z-10">
            <img
              src={currentEvent.organizerImage}
              alt={currentEvent.organizerName || "Organizer"}
              className="w-8 h-8 md:w-12 md:h-12 rounded-full border-2 border-yellow-200 object-cover"
              title={currentEvent.organizerName}
            />
          </div>
        )}

        {/* Sponsored Badge - Fixed at top center */}
        <div className="absolute top-2 left-0 right-0 flex justify-center z-10">
          <span className="bg-yellow-400 text-purple-900 text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full uppercase tracking-wide shadow-lg">
            ⭐ Evento Sponsorizzato
          </span>
        </div>

        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center justify-between px-4 md:px-8">
          {/* Navigation arrows (only show if multiple events) */}
          {activeEvents.length > 1 && (
            <button
              onClick={handlePrevious}
              className="flex-shrink-0 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
              aria-label="Evento precedente"
            >
              <ChevronLeftIcon className="h-6 w-6 text-white" />
            </button>
          )}

          {/* Event title */}
          <div className="flex-1 mx-4 text-center">
            <h3 className="text-white font-bold text-lg md:text-2xl drop-shadow-lg line-clamp-2">
              {currentEvent.title}
            </h3>
            {activeEvents.length > 1 && (
              <div className="flex justify-center gap-2 mt-2">
                {activeEvents.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 rounded-full transition-all ${
                      idx === currentIndex
                        ? "w-8 bg-white"
                        : "w-2 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Navigation arrows (only show if multiple events) */}
          {activeEvents.length > 1 && (
            <button
              onClick={handleNext}
              className="flex-shrink-0 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-colors"
              aria-label="Evento successivo"
            >
              <ChevronRightIcon className="h-6 w-6 text-white" />
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 z-30 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full p-1.5 transition-colors"
          aria-label="Chiudi banner"
        >
          <XMarkIcon className="h-5 w-5 text-white" />
        </button>
      </div>
    </div>
  );
};

export default AdvertisedEventBanner;
